/**
 * F 128 Cut 1 — markdown RENDERER (d000697 §2).
 *
 * This is Cut 1 of the markdown platform primitive and it is deliberately the
 * renderer ALONE. No `markdown -> Block[]` parser (Cut 2b), no editor (Cut 3),
 * no paste pipeline (Cut 3), no emoji/icon picker UI (Cut 3). `:icon[name]:`
 * renders as an inert placeholder span here.
 *
 * Why markdown-it and not a hand-rolled parser (d000697 §1):
 *   - `html: false` makes XSS-safety the DEFAULT path, not an optional plugin
 *     a consumer can forget. For a library shipping to VC, Gallery and
 *     Homeschool, "the safe path is the path you get for free" is the whole
 *     argument. remark + rehype requires remembering `rehype-sanitize`.
 *   - It is a strict SUPERSET of the hand-rolled subset we are replacing, so
 *     "match or exceed" against the acceptance bar is structural, not argued.
 *
 * Sync, not async: VibeChat streams by re-rendering on each delta. An async
 * renderer would force a change to that loop, which is exactly the churn the
 * adoption pattern exists to avoid.
 */

import MarkdownIt from 'markdown-it';
import type StateInline from 'markdown-it/lib/rules_inline/state_inline.mjs';
import { sanitizeMarkdownOutput } from './allowlist';

// ---------------------------------------------------------------------------
// Public types (d000697 §2)
// ---------------------------------------------------------------------------

/**
 * VC ships three chat profiles; d000697 adds a fourth for documents.
 * Chat never needs `document` (tables, images, footnotes at full fidelity);
 * CMS and gallery docs do.
 */
export type RenderProfile = 'compact' | 'standard' | 'focus' | 'document';

export interface RenderOptions {
  /** Append a loading placeholder. Matches VC's streaming affordance. */
  streaming?: boolean;
  /** Enable `:icon[name]:` placeholders. Default true. */
  icons?: boolean;
  /**
   * Resolve an `fcid://` URI to an href. Return null (or omit) to render the
   * reference as inert text marked `data-fc-ref-missing` — a document must
   * always render, even when every cross-ref is dead (d000697 §1).
   */
  resolveRef?: (uri: string) => string | null;
  /** Prefix applied to `§NN` chips. Default '#'. */
  sectionHrefPrefix?: string;
  /** Escape hatch: skip the output allowlist. Never use on untrusted input. */
  skipSanitize?: boolean;
}

// ---------------------------------------------------------------------------
// Profile wrappers — class names and spacing, per d000697 §2
// ---------------------------------------------------------------------------

interface ProfileSpec {
  wrapperClass: string;
  /** Classes added to the root element. */
  rootClass: string;
}

const PROFILES: Record<RenderProfile, ProfileSpec> = {
  compact:  { wrapperClass: 'fc-md', rootClass: 'fc-md--compact' },
  standard: { wrapperClass: 'fc-md', rootClass: 'fc-md--standard' },
  focus:    { wrapperClass: 'fc-md', rootClass: 'fc-md--focus' },
  document: { wrapperClass: 'fc-md fc-md--doc', rootClass: 'fc-md--document' },
};

export const RENDER_PROFILES: readonly RenderProfile[] = ['compact', 'standard', 'focus', 'document'];

// ---------------------------------------------------------------------------
// The FCr plugin — §NN, fcid:// and :icon[name]: in ONE rule (d000697 §1)
// ---------------------------------------------------------------------------

/** `fcid://<kind>/<id>` — kind and id are URL-safe token characters. */
const FCID_RE = /fcid:\/\/([a-zA-Z0-9_-]+)\/([a-zA-Z0-9._-]+)/g;
/** `§NN` doctrine section refs, optionally with a suffix like §11.9 */
const SECTION_RE = /§(\d+(?:\.\d+)*)/g;
/** `:icon[name]:` — name is a restricted token, never interpolated raw. */
const ICON_RE = /:icon\[([a-zA-Z0-9._-]{1,64})\]:/g;

const ICON_NAME_RE = /^[a-zA-Z0-9._-]{1,64}$/;

function esc(s: string): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

interface FcrPluginState {
  opts: Required<Pick<RenderOptions, 'icons' | 'sectionHrefPrefix' | 'resolveRef'>>;
}

/**
 * A single inline rule covering all three FCr forms. Registered as a
 * 'text' rule with a high priority so it runs before markdown-it's own inline
 * handling mangles the tokens.
 */
function fcrInline(state: StateInline, silent: boolean): boolean {
  const { src, pos, posMax } = state;
  const cfg = (state.env as { fcr?: FcrPluginState }).fcr;
  if (!cfg) return false;
  const { opts } = cfg;

  // The cheapest test first: this rule only fires on these three lead bytes.
  const ch = src.charCodeAt(pos);
  const leadIsSection = ch === 0xa7;                    // §
  const leadIsFcid = ch === 0x66 && src.startsWith('fcid://', pos);   // f
  const leadIsIcon = ch === 0x3a && src.startsWith(':icon[', pos);     // :

  if (!leadIsSection && !leadIsFcid && !leadIsIcon) return false;

  if (leadIsSection) {
    SECTION_RE.lastIndex = pos;
    const m = SECTION_RE.exec(src);
    if (!m || m.index !== pos || m[0].length === 0) return false;
    if (!silent) {
      const section = m[1];
      const token = state.push('fcr_section', 'span', 0);
      token.content = m[0];
      token.meta = { section, href: `${opts.sectionHrefPrefix}${section}` };
    }
    state.pos = pos + m[0].length;
    return true;
  }

  if (leadIsFcid) {
    FCID_RE.lastIndex = pos;
    const m = FCID_RE.exec(src);
    if (!m || m.index !== pos) return false;
    if (!silent) {
      const uri = m[0];
      const kind = m[1];
      const id = m[2];
      // Resolution is the CONSUMER's job (d000697 §1). Unresolvable refs
      // degrade to inert text — a document must always render.
      let href: string | null = null;
      try { href = opts.resolveRef(uri); } catch { href = null; }
      if (href && typeof href === 'string' && isSafeHref(href)) {
        const token = state.push('fcr_ref_link', 'a', 0);
        token.content = m[0];
        token.attrSet('href', href);
        token.attrSet('data-fc-ref', `${kind}:${id}`);
      } else {
        const token = state.push('fcr_ref_missing', 'span', 0);
        token.content = m[0];
        token.attrSet('data-fc-ref', `${kind}:${id}`);
        token.attrSet('data-fc-ref-missing', '1');
      }
    }
    state.pos = pos + m[0].length;
    return true;
  }

  // :icon[name]: — Cut 1 renders an INERT placeholder. The icon sprite and
  // the picker UI are Cut 3. The name is validated against a strict pattern
  // and escaped regardless; it is never interpolated into markup.
  ICON_RE.lastIndex = pos;
  const m = ICON_RE.exec(src);
  if (!m || m.index !== pos) return false;
  if (!silent) {
    const name = m[1];
    if (opts.icons && ICON_NAME_RE.test(name)) {
      const token = state.push('fcr_icon', 'span', 0);
      token.content = '';
      token.attrSet('data-fc-icon', name);
      token.attrSet('aria-label', name);
      token.attrSet('role', 'img');
    } else {
      // Icons off, or a name we do not accept: inert text, never markup.
      const token = state.push('text', '', 0);
      token.content = m[0];
    }
  }
  state.pos = pos + m[0].length;
  return true;
}

function isSafeHref(href: string): boolean {
  const v = href.trim().toLowerCase().replace(/[\u0000-\u0020]/g, '');
  return !(v.startsWith('javascript:') || v.startsWith('vbscript:') || v.startsWith('data:'));
}

// ---------------------------------------------------------------------------
// Extending the `text` rule's terminator set
// ---------------------------------------------------------------------------

/** markdown-it's own terminator set, copied so we extend it rather than replace it. */
function isBaseTerminator(ch: number): boolean {
  switch (ch) {
    case 0x0A: case 0x21: case 0x23: case 0x24: case 0x25: case 0x26:
    case 0x2A: case 0x2B: case 0x2D: case 0x3A: case 0x3C: case 0x3D:
    case 0x3E: case 0x40: case 0x5B: case 0x5C: case 0x5D: case 0x5E:
    case 0x5F: case 0x60: case 0x7B: case 0x7D: case 0x7E:
      return true;
    default:
      return false;
  }
}

/**
 * Why this exists: markdown-it's `text` rule consumes a whole run of plain
 * characters up to the next TERMINATOR character, and neither `\u00a7` (section
 * sign) nor `f` is one. So it swallowed `\u00a711` and `fcid://...` as ordinary
 * text, and the FCr rule was never offered a position where its token began.
 *
 * `ruler.before('text', ...)` alone is therefore not enough — a rule is only
 * consulted at positions the text rule has not already consumed. This
 * replaces `text` with the same behaviour plus two extra stops:
 *
 *   - `\u00a7` always; it starts no markdown construct
 *   - the literal `fcid://` — NOT a bare `f`. Stopping on every `f` would shred
 *     ordinary prose ("f(x)", "for", "5 fish").
 */
function fcrTerminator(src: string, pos: number): boolean {
  const ch = src.charCodeAt(pos);
  if (isBaseTerminator(ch)) return true;
  if (ch === 0xa7) return true;
  if (ch === 0x66 /* f */ && src.startsWith('fcid://', pos)) return true;
  return false;
}

function fcrTextRule(state: StateInline, silent: boolean): boolean {
  const { src, posMax } = state;
  let pos = state.pos;
  while (pos < posMax && !fcrTerminator(src, pos)) {
    // A backslash escapes the next char, so an escaped char is never a stop.
    if (src.charCodeAt(pos) === 0x5c) { pos += 2; continue; }
    pos++;
  }
  if (pos === state.pos) return false;
  if (!silent) state.pending += src.slice(state.pos, pos);
  state.pos = pos;
  return true;
}

// ---------------------------------------------------------------------------
// The single markdown-it instance (Principle 4: one renderer, no forks)
// ---------------------------------------------------------------------------

export function createInstance(opts: RenderOptions): MarkdownIt {
  const md = new MarkdownIt({
    // Layer 1 of §2's three: raw HTML is escaped, never passed through.
    html: false,
    // Rejects javascript: / data: hrefs at the source. Layer 2.
    linkify: true,
    breaks: true,
    typographer: false,
  });

  const state: FcrPluginState = {
    opts: {
      icons: opts.icons !== false,
      sectionHrefPrefix: opts.sectionHrefPrefix ?? '#',
      resolveRef: opts.resolveRef ?? (() => null),
    },
  };
  // markdown-it takes `env` as a RENDER argument, not as instance state —
  // `md.env` is undefined on a fresh instance. The plugin state is threaded
  // through render(src, env) and reaches the rule as `state.env`.
  md.inline.ruler.before('text', 'fcr_inline', fcrInline);
  md.inline.ruler.at('text', fcrTextRule);
  md.renderer.__fcrEnv = { fcr: state };

  md.renderer.rules.fcr_section = (tokens, idx) => {
    const t = tokens[idx];
    const href = (t.meta as { href?: string } | undefined)?.href ?? '#';
    return `<span class="fc-md__ref" data-fc-section="${esc((t.meta as { section?: string })?.section ?? '')}">`
      + `<a class="fc-md__ref-link" href="${esc(href)}">${esc(t.content)}</a></span>`;
  };
  md.renderer.rules.fcr_ref_link = (tokens, idx) => {
    const t = tokens[idx];
    return `<a class="fc-md__ref-link" href="${esc(t.attrGet('href') ?? '#')}"`
      + ` data-fc-ref="${esc(t.attrGet('data-fc-ref') ?? '')}">${esc(t.content)}</a>`;
  };
  md.renderer.rules.fcr_ref_missing = (tokens, idx) => {
    const t = tokens[idx];
    return `<span class="fc-md__ref fc-md__ref--missing" data-fc-ref="${esc(t.attrGet('data-fc-ref') ?? '')}"`
      + ` data-fc-ref-missing="1" title="Unresolved reference">${esc(t.content)}</span>`;
  };
  md.renderer.rules.fcr_icon = (_tokens, idx) => {
    const t = _tokens[idx];
    // Placeholder only. No <svg>, no interpolated name, no src.
    return `<span class="fc-md__icon" data-fc-icon="${esc(t.attrGet('data-fc-icon') ?? '')}"`
      + ` role="img" aria-label="${esc(t.attrGet('aria-label') ?? '')}"></span>`;
  };

  return md;
}

// ---------------------------------------------------------------------------
// Primary API
// ---------------------------------------------------------------------------

/**
 * Render markdown to an HTML string.
 *
 * Same signature as VC's renderer, so swapping VibeChat over is an import
 * change and nothing else (d000697 §7).
 *
 * Never throws. Bad input degrades to escaped text — a renderer that throws
 * takes a chat surface down with it.
 */
/**
 * The single markdown-it instance factory (Principle 4: one parser).
 *
 * F 129's `markdown -> Block[]` parser uses THIS, not a second parser. Two
 * instances would mean two rule tables, and the FCr inline plugin would live
 * in one and not the other — the exact fork d000697 forbids.
 */
export { createInstance as markdownItInstance };

export function renderMarkdown(
  md: string,
  profile: RenderProfile = 'standard',
  opts: RenderOptions = {},
): string {
  if (typeof md !== 'string' || md.length === 0) {
    return opts.streaming ? renderPlaceholder(profile) : '';
  }
  // Normalise BEFORE anything reads it: an unknown value must not leak into
  // the data-fc-profile attribute as a consumer-visible string.
  const resolved: RenderProfile =
    (RENDER_PROFILES as readonly string[]).includes(profile) ? profile : 'standard';
  const spec = PROFILES[resolved];
  let body: string;
  try {
    const instance = createInstance(opts);
    body = instance.render(md, instance.renderer.__fcrEnv);
  } catch {
    // Degrade loudly-but-safely: escaped source, still inside the wrapper.
    const esc = md.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    body = `<p>${esc}</p>`;
  }

  // Layer 3 of §2's three: the output allowlist, over OUR OWN output.
  if (!opts.skipSanitize) {
    try { body = sanitizeMarkdownOutput(body); }
    catch { /* an unsanitised body still came from html:false, keep it */ }
  }

  const root = `<div class="${spec.wrapperClass} ${spec.rootClass}" data-fc-profile="${resolved}">`;
  const tail = opts.streaming ? renderStreamingTail() : '';
  return `${root}${body}${tail}</div>`;
}

function renderPlaceholder(profile: RenderProfile): string {
  const spec = PROFILES[profile] ?? PROFILES.standard;
  return `<div class="${spec.wrapperClass} ${spec.rootClass}" data-fc-profile="${profile}">`
    + `<span class="fc-md__loading" data-fc-loading="1"></span></div>`;
}

function renderStreamingTail(): string {
  return `<span class="fc-md__streaming" data-fc-streaming="1" aria-busy="true"></span>`;
}

/**
 * Secondary API for consumers that need the AST rather than a string.
 * Same instance, same options — one parser, two outputs (Principle 4).
 */
export function parseMarkdownToAst(md: string, opts: RenderOptions = {}): MarkdownIt.Token[] {
  if (typeof md !== 'string' || md.length === 0) return [];
  const inst = createInstance(opts);
  return inst.parse(md, inst.renderer.__fcrEnv);
}

export { sanitizeMarkdownOutput, MARKDOWN_ALLOWED_TAGS } from './allowlist';
